export type SeasonalTheme = 'christmas' | 'diwali';

/**
 * Diwali moves every year (lunar calendar), so each year's window is listed
 * here. Add next year's dates before the festival. Dates are inclusive and
 * compared against the viewer's local date.
 */
export const DIWALI_WINDOWS: ReadonlyArray<{ start: string; end: string }> = [
  { start: '2026-11-05', end: '2026-11-10' }, // Diwali: Sun, Nov 8 2026
];

// Countries that celebrate Diwali (Deepavali), by IANA zone:
// - India: "Asia/Calcutta" is the legacy alias some browsers still report.
// - Singapore: "Singapore" is the legacy alias.
// - Malaysia: Kuala Lumpur (peninsula) and Kuching (Sabah and Sarawak).
const DIWALI_TIME_ZONES = new Set([
  'Asia/Kolkata',
  'Asia/Calcutta',
  'Asia/Singapore',
  'Singapore',
  'Asia/Kuala_Lumpur',
  'Asia/Kuching',
]);

export const isDiwaliTimeZone = (timeZone: string | null | undefined) =>
  !!timeZone && DIWALI_TIME_ZONES.has(timeZone);

export const getBrowserTimeZone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
};

const toLocalIsoDate = (date: Date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const isDiwaliWindow = (date: Date) => {
  const today = toLocalIsoDate(date);
  return DIWALI_WINDOWS.some(
    ({ start, end }) => today >= start && today <= end
  );
};

// Dec 1 – Jan 9, for everyone.
const isChristmasWindow = (date: Date) => {
  const month = date.getMonth();
  return month === 11 || (month === 0 && date.getDate() < 10);
};

/**
 * Which festive look the top nav shows today, if any. Diwali only shows for
 * viewers whose browser is set to India, Singapore or Malaysia time.
 */
export const getSeasonalTheme = (
  date: Date,
  timeZone: string | null | undefined
): SeasonalTheme | null => {
  if (isChristmasWindow(date)) return 'christmas';
  if (isDiwaliWindow(date) && isDiwaliTimeZone(timeZone)) return 'diwali';
  return null;
};
